import { useState, useEffect } from 'react'
import axios from 'src/utilities/http-client'

type VttSubtitlesState = {
  vttUrl: string | null
  isLoading: boolean
  error: string | null
}

/**
 * Hook to fetch and load VTT subtitles for a video
 *
 * @param videoId The ID of the video to fetch subtitles for
 * @returns An object with the VTT URL, loading state, and error state
 *
 * Usage:
 * ```tsx
 * const { vttUrl, isLoading, error } = useVttSubtitles(videoId)
 *
 * // In your MuxPlayer component:
 * <MuxPlayer>
 *   {vttUrl && (
 *     <track
 *       src={vttUrl}
 *       kind="subtitles"
 *       label="English"
 *       srcLang="en"
 *       default
 *     />
 *   )}
 * </MuxPlayer>
 * ```
 */
export const useVttSubtitles = (
  videoId: string | undefined,
): VttSubtitlesState => {
  let [state, setState] = useState<VttSubtitlesState>({
    vttUrl: null,
    isLoading: false,
    error: null,
  })

  useEffect(() => {
    // Reset state when videoId changes
    setState({
      vttUrl: null,
      isLoading: false,
      error: null,
    })

    // Don't attempt to fetch if videoId is undefined
    if (!videoId) {
      return
    }

    let fetchVttUrl = async () => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }))

      try {
        // Try to get the corrected VTT URL
        let response = await axios.get(
          `/api/classroom/video/${videoId}/transcript/vtt`,
        )

        if (response?.data) {
          setState({
            vttUrl: response.data,
            isLoading: false,
            error: null,
          })
        } else {
          setState({
            vttUrl: null,
            isLoading: false,
            error: 'Invalid response for VTT URL',
          })
        }
      } catch {
        // It's expected that the VTT might not exist yet, so we don't show an error to the user
        setState({
          vttUrl: null,
          isLoading: false,
          error: null, // We don't treat this as an error state as it's expected
        })
      }
    }

    fetchVttUrl()
  }, [videoId])

  return state
}

export default useVttSubtitles
